"use client";

import { createContext, useContext, useEffect, useState } from "react";
import type { ErrorContext, ErrorProvider, LogLevel } from "@/lib/errors/types";
import { nullErrorProvider } from "@/lib/errors/providers/null";
import { envClient } from "@/lib/config/env.client";
import { sanitize } from "@/lib/infra/logger";

const ErrorTrackingContext = createContext<ErrorProvider>(nullErrorProvider);

let clientErrorProvider: ErrorProvider = nullErrorProvider;

export function reportClientError(error: unknown, context?: ErrorContext): void {
  const safeContext = context ? (sanitize(context) as ErrorContext) : undefined;
  void clientErrorProvider.captureException(error, safeContext);
}

export function reportClientLog(
  level: LogLevel,
  message: string,
  context?: ErrorContext
): void {
  if (!envClient.NEXT_PUBLIC_ERROR_LOGS) return;
  const safeContext = context ? (sanitize(context) as ErrorContext) : undefined;
  void clientErrorProvider.captureLog?.(level, message, safeContext);
}

export function useErrorTracking(): ErrorProvider {
  return useContext(ErrorTrackingContext);
}

export function ErrorTrackingClientProvider({
  enabled,
  provider,
  children,
}: {
  enabled: boolean;
  provider?: string;
  children: React.ReactNode;
}) {
  const [activeProvider, setActiveProvider] =
    useState<ErrorProvider>(nullErrorProvider);

  useEffect(() => {
    let active = true;
    if (!enabled) {
      clientErrorProvider = nullErrorProvider;
      setActiveProvider(nullErrorProvider);
      return;
    }

    const load = async () => {
      switch (provider) {
        case "sentry":
        case "bugsink": {
          // Bugsink is Sentry-protocol compatible; reuse the Sentry client with a Bugsink DSN.
          const dsn = envClient.NEXT_PUBLIC_ERROR_DSN;
          if (!dsn) {
            clientErrorProvider = nullErrorProvider;
            setActiveProvider(nullErrorProvider);
            return;
          }
          const { SentryClientProvider } = await import(
            "@/lib/errors/providers/sentry.client"
          );
          if (active) {
            const instance = new SentryClientProvider(dsn);
            clientErrorProvider = instance;
            setActiveProvider(instance);
          }
          break;
        }
        case "betterstack": {
          // Bugsink/Better Stack errors reuse the Sentry client; Better Stack logs go to Logtail.
          const dsn = envClient.NEXT_PUBLIC_ERROR_DSN;
          if (!dsn) {
            clientErrorProvider = nullErrorProvider;
            setActiveProvider(nullErrorProvider);
            return;
          }
          const { BetterStackClientProvider } = await import(
            "@/lib/errors/providers/betterstack.client"
          );
          if (active) {
            const instance = new BetterStackClientProvider(dsn);
            clientErrorProvider = instance;
            setActiveProvider(instance);
          }
          break;
        }
        case "posthog": {
          const key = envClient.NEXT_PUBLIC_POSTHOG_KEY;
          if (!key) {
            clientErrorProvider = nullErrorProvider;
            setActiveProvider(nullErrorProvider);
            return;
          }
          const { PostHogClientErrorProvider } = await import(
            "@/lib/errors/providers/posthog.client"
          );
          if (active) {
            const instance = new PostHogClientErrorProvider(
              key,
              envClient.NEXT_PUBLIC_POSTHOG_HOST
            );
            clientErrorProvider = instance;
            setActiveProvider(instance);
          }
          break;
        }
        default:
          clientErrorProvider = nullErrorProvider;
          setActiveProvider(nullErrorProvider);
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [enabled, provider]);

  return (
    <ErrorTrackingContext.Provider value={activeProvider}>
      {children}
    </ErrorTrackingContext.Provider>
  );
}
